import React, { useState, useRef } from 'react';
import { 
  Music, 
  Calendar, 
  Clock, 
  MapPin, 
  DollarSign, 
  FileText, 
  User, 
  Phone, 
  Mail, 
  X, 
  Upload, 
  Image, 
  ChevronDown, 
  ChevronUp,
  Palette,
  CalendarRange,
  Trash2,
  Repeat
} from 'lucide-react';

// Event type configurations
const EVENT_TYPES = {
  show: { label: 'Show', icon: Music, color: 'bg-blue-600 hover:bg-blue-700' },
  other: { label: 'Other', icon: Calendar, color: 'bg-blue-600 hover:bg-blue-700' }
};

// Color options for other events
const EVENT_COLORS = [
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500', light: 'bg-orange-100 dark:bg-orange-900/30' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-500', light: 'bg-purple-100 dark:bg-purple-900/30' },
  { value: 'green', label: 'Green', bg: 'bg-green-500', light: 'bg-green-100 dark:bg-green-900/30' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-500', light: 'bg-pink-100 dark:bg-pink-900/30' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500', light: 'bg-cyan-100 dark:bg-cyan-900/30' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500', light: 'bg-yellow-100 dark:bg-yellow-900/30' },
  { value: 'red', label: 'Red', bg: 'bg-red-500', light: 'bg-red-100 dark:bg-red-900/30' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500', light: 'bg-indigo-100 dark:bg-indigo-900/30' },
];

// Recurrence patterns
const RECURRENCE_PATTERNS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'S', fullLabel: 'Sunday' },
  { value: 1, label: 'M', fullLabel: 'Monday' },
  { value: 2, label: 'T', fullLabel: 'Tuesday' },
  { value: 3, label: 'W', fullLabel: 'Wednesday' },
  { value: 4, label: 'T', fullLabel: 'Thursday' },
  { value: 5, label: 'F', fullLabel: 'Friday' },
  { value: 6, label: 'S', fullLabel: 'Saturday' },
];

// Contact tags
const CONTACT_TAGS = [
  { value: 'venue', label: 'Venue', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'promoter', label: 'Promoter', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  { value: 'booking', label: 'Booking Agent', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  { value: 'sound', label: 'Sound Engineer', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
];

const getEventTypeConfig = (type) => EVENT_TYPES[type] || EVENT_TYPES.show;

const getEventDayCount = (event) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) return 1;
  const start = new Date(event.startDate);
  const end = new Date(event.endDate);
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

// Collapsible Section Component
const Section = ({ title, icon: Icon, expanded, onToggle, children, badge }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden relative" style={{ isolation: 'isolate' }}>
    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
      className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative z-10"
      style={{ touchAction: 'manipulation' }}>
      <div className="flex items-center gap-2 pointer-events-none">
        <Icon size={18} className="text-gray-600 dark:text-gray-400" />
        <span className="font-medium text-gray-700 dark:text-gray-300">{title}</span>
        {badge && <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs rounded-full">{badge}</span>}
      </div>
      <div className="pointer-events-none">
        {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </div>
    </button>
    {expanded && <div className="p-4 space-y-4 relative z-0">{children}</div>}
  </div>
);

// Image Upload Component
const ImageUpload = ({ images = [], onImagesChange, maxImages = 3 }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const newImages = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (images.length + newImages.length >= maxImages) break;
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        newImages.push({ id: Date.now() + Math.random(), name: file.name, type: file.type, size: file.size, data: dataUrl, uploadedAt: new Date().toISOString() });
      } catch (err) { console.error('Error reading file:', err); }
    }
    onImagesChange([...images, ...newImages]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (imageId) => onImagesChange(images.filter(img => img.id !== imageId));

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map(img => (
            <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
              <img src={img.data} alt={img.name} className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeImage(img.id)} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {images.length < maxImages && (
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 hover:border-purple-400 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors disabled:opacity-50">
            {uploading ? <><div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Uploading...</> : <><Upload size={20} />Add Flyer / Advance</>}
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">{images.length} of {maxImages} images</p>
        </div>
      )}
    </div>
  );
};

// Multi-Day Toggle Component
const MultiDayToggle = ({ showForm, handleChange }) => {
  const isMultiDay = showForm.isMultiDay || false;
  const handleToggle = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!isMultiDay) { handleChange('isMultiDay', true); if (!showForm.endDate && showForm.startDate) handleChange('endDate', showForm.startDate); }
    else { handleChange('isMultiDay', false); handleChange('startDate', null); handleChange('endDate', null); }
  };
  const dayCount = isMultiDay && showForm.startDate && showForm.endDate ? getEventDayCount({ isMultiDay: true, startDate: showForm.startDate, endDate: showForm.endDate }) : 1;

  return (
    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 mb-4 relative" style={{ isolation: 'isolate' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarRange size={16} className="text-purple-600 dark:text-purple-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Multi-Day Event</span>
          {isMultiDay && dayCount > 1 && <span className="px-2 py-0.5 bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 text-xs rounded-full">{dayCount} days</span>}
        </div>
        <button type="button" onClick={handleToggle} className={`relative w-10 h-5 rounded-full transition-colors ${isMultiDay ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`} style={{ touchAction: 'manipulation' }}>
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isMultiDay ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
      {isMultiDay && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
            <input type="date" value={showForm.startDate || ''} onChange={(e) => { handleChange('startDate', e.target.value); if (showForm.endDate && e.target.value > showForm.endDate) handleChange('endDate', e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-purple-500 text-sm" style={{ touchAction: 'manipulation' }} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
            <input type="date" value={showForm.endDate || ''} min={showForm.startDate} onChange={(e) => handleChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-purple-500 text-sm" style={{ touchAction: 'manipulation' }} />
          </div>
        </div>
      )}
    </div>
  );
};

// Recurrence Section Component
const RecurrenceSection = ({ showForm, handleChange }) => {
  const currentPattern = showForm.recurrence?.pattern || 'none';
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-4 relative" style={{ isolation: 'isolate' }}>
      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
        <Repeat size={18} />
        <span className="font-medium">Repeat</span>
        {currentPattern !== 'none' && <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs rounded-full">{RECURRENCE_PATTERNS.find(p => p.value === currentPattern)?.label}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {RECURRENCE_PATTERNS.map(pattern => (
          <button key={pattern.value} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleChange('recurrence', { ...showForm.recurrence, pattern: pattern.value, daysOfWeek: pattern.value === 'weekly' ? (showForm.recurrence?.daysOfWeek || []) : undefined }); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${currentPattern === pattern.value ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            style={{ touchAction: 'manipulation' }}>{pattern.label}</button>
        ))}
      </div>
      {currentPattern === 'weekly' && (
        <div className="space-y-2">
          <label className="block text-sm text-gray-500 dark:text-gray-400">Repeat on</label>
          <div className="flex gap-2">
            {DAYS_OF_WEEK.map(day => {
              const isSelected = (showForm.recurrence?.daysOfWeek || []).includes(day.value);
              return (
                <button key={day.value} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); const currentDays = showForm.recurrence?.daysOfWeek || []; const newDays = isSelected ? currentDays.filter(d => d !== day.value) : [...currentDays, day.value]; handleChange('recurrence', { ...showForm.recurrence, daysOfWeek: newDays.sort((a, b) => a - b) }); }}
                  className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                  style={{ touchAction: 'manipulation' }} title={day.fullLabel}>{day.label}</button>
              );
            })}
          </div>
        </div>
      )}
      {currentPattern !== 'none' && (
        <div className="space-y-2">
          <label className="block text-sm text-gray-500 dark:text-gray-400">End repeat (optional)</label>
          <input type="date" value={showForm.recurrence?.endDate || ''} onChange={(e) => handleChange('recurrence', { ...showForm.recurrence, endDate: e.target.value || undefined })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500" style={{ touchAction: 'manipulation' }} />
          <p className="text-xs text-gray-400 dark:text-gray-500">Leave blank to create 12 occurrences</p>
        </div>
      )}
    </div>
  );
};

// Main Form Component
const ShowFormComponent = ({ showForm, setShowForm, editingShow, onSubmit, onCancel, onDelete, isPastOrCompleted = false }) => {
  const [sections, setSections] = useState({ basic: true, bands: false, documents: false, contact: false, notes: false, financial: false });
  const [newBandName, setNewBandName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const toggleSection = (section) => setSections(prev => ({ ...prev, [section]: !prev[section] }));
  const handleChange = (field, value) => setShowForm(prev => ({ ...prev, [field]: value }));
  const toggleContactTag = (tagValue) => { const currentTags = showForm.contactTags || []; handleChange('contactTags', currentTags.includes(tagValue) ? currentTags.filter(t => t !== tagValue) : [...currentTags, tagValue]); };
  const addBand = () => { if (newBandName.trim()) { handleChange('bands', [...(showForm.bands || []), newBandName.trim()]); setNewBandName(''); } };
  const removeBand = (index) => handleChange('bands', (showForm.bands || []).filter((_, i) => i !== index));
  const handleBandKeyPress = (e) => { if (e.key === 'Enter') { e.preventDefault(); addBand(); } };
  const handleImagesChange = (newImages) => handleChange('images', newImages);
  const handleDeleteClick = (e) => { e.preventDefault(); e.stopPropagation(); setShowDeleteConfirm(true); };
  const handleConfirmDelete = () => { if (onDelete && editingShow) onDelete(editingShow); setShowDeleteConfirm(false); };

  const eventType = showForm.eventType || 'show';
  const config = getEventTypeConfig(eventType);
  const isShow = eventType === 'show';
  const isCompleted = showForm.status === 'completed';
  const imageCount = (showForm.images || []).length;
  const showFinancials = editingShow && isCompleted;
  const showRecurrence = !editingShow;

  return (
    <div className="space-y-4 pb-24 md:pb-4" style={{ touchAction: 'pan-y' }}>
      <MultiDayToggle showForm={showForm} handleChange={handleChange} />

      <div className="flex gap-2 relative" style={{ isolation: 'isolate' }}>
        {Object.entries(EVENT_TYPES).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button key={key} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleChange('eventType', key); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-medium transition-all ${eventType === key ? `${cfg.color} text-white` : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
              style={{ touchAction: 'manipulation' }}><Icon size={18} />{cfg.label}</button>
          );
        })}
      </div>

      {isShow && (
        <>
          <div className="relative" style={{ isolation: 'isolate' }}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
            <div className="flex gap-2">
              {['tentative', 'confirmed', 'completed'].map(status => (
                <button key={status} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleChange('status', status); }}
                  className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-colors ${showForm.status === status ? (status === 'tentative' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' : status === 'confirmed' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' : 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200') : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                  style={{ touchAction: 'manipulation' }}>{status.charAt(0).toUpperCase() + status.slice(1)}</button>
              ))}
            </div>
          </div>

          <div className="relative" style={{ isolation: 'isolate' }}>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Show Title *</label>
            <input type="text" placeholder="e.g., The Bitter End, SXSW 2025..." value={showForm.title || ''} onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
          </div>

          {showRecurrence && <RecurrenceSection showForm={showForm} handleChange={handleChange} />}

          <div className="grid grid-cols-2 gap-3 relative" style={{ isolation: 'isolate' }}>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Venue</label>
              <input type="text" placeholder="Venue name" value={showForm.venue || ''} onChange={(e) => handleChange('venue', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">City</label>
              <input type="text" placeholder="City, State" value={showForm.city || ''} onChange={(e) => handleChange('city', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 relative" style={{ isolation: 'isolate' }}>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Load In Time</label>
              <input type="time" value={showForm.loadInTime || ''} onChange={(e) => handleChange('loadInTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Show Time</label>
              <input type="time" value={showForm.showTime || ''} onChange={(e) => handleChange('showTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
            </div>
          </div>

          <Section title="Bands / Artists" icon={Music} expanded={sections.bands} onToggle={() => toggleSection('bands')} badge={(showForm.bands || []).length > 0 ? (showForm.bands || []).length : null}>
            {(showForm.bands || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {(showForm.bands || []).map((band, index) => (
                  <span key={index} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                    {band}<button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeBand(index); }} className="p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full" style={{ touchAction: 'manipulation' }}><X size={14} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input type="text" placeholder="Add band name..." value={newBandName} onChange={(e) => setNewBandName(e.target.value)} onKeyPress={handleBandKeyPress}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); addBand(); }} disabled={!newBandName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl disabled:opacity-50 hover:bg-blue-700 transition-colors" style={{ touchAction: 'manipulation' }}>Add</button>
            </div>
          </Section>

          <Section title="Flyers & Documents" icon={Image} expanded={sections.documents} onToggle={() => toggleSection('documents')} badge={imageCount > 0 ? imageCount : null}>
            <ImageUpload images={showForm.images || []} onImagesChange={handleImagesChange} maxImages={3} />
          </Section>

          <Section title="Contact" icon={User} expanded={sections.contact} onToggle={() => toggleSection('contact')}>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Name</label>
                <input type="text" placeholder="Contact name" value={showForm.contactName || ''} onChange={(e) => handleChange('contactName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Email</label>
                  <input type="email" placeholder="email@example.com" value={showForm.contactEmail || ''} onChange={(e) => handleChange('contactEmail', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Phone</label>
                  <input type="tel" placeholder="(555) 123-4567" value={showForm.contactPhone || ''} onChange={(e) => handleChange('contactPhone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500" style={{ touchAction: 'manipulation' }} />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {CONTACT_TAGS.map(tag => (
                    <button key={tag.value} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleContactTag(tag.value); }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${(showForm.contactTags || []).includes(tag.value) ? tag.color + ' ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-gray-800' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'}`}
                      style={{ touchAction: 'manipulation' }}>{tag.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          <Section title="Notes" icon={FileText} expanded={sections.notes} onToggle={() => toggleSection('notes')}>
            <textarea placeholder="Any notes..." value={showForm.showNotes || ''} onChange={(e) => handleChange('showNotes', e.target.value)} rows={3}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500 resize-none" style={{ touchAction: 'manipulation' }} />
          </Section>

          {showFinancials && (
            <Section title="Financials" icon={DollarSign} expanded={sections.financial} onToggle={() => toggleSection('financial')}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Record your earnings and expenses</p>
              <div className="grid grid-cols-3 gap-3">
                {[{ label: 'Guarantee', field: 'guarantee' }, { label: 'Cash Merch', field: 'cashMerch' }, { label: 'Venmo Merch', field: 'venmoMerch' }].map(({ label, field }) => (
                  <div key={field}>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                      <input type="number" placeholder="0" value={showForm[field] || ''} onChange={(e) => handleChange(field, e.target.value)}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500" style={{ touchAction: 'manipulation' }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                {[{ label: 'Gas', field: 'gas' }, { label: 'Hotel', field: 'hotel' }, { label: 'Food', field: 'food' }].map(({ label, field }) => (
                  <div key={field}>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                      <input type="number" placeholder="0" value={showForm[field] || ''} onChange={(e) => handleChange(field, e.target.value)}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500" style={{ touchAction: 'manipulation' }} />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      {!isShow && (
        <div className="space-y-4">
          <div className="relative" style={{ isolation: 'isolate' }}>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Event Title *</label>
            <input type="text" placeholder="e.g., Band Practice, Studio Session..." value={showForm.title || ''} onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-orange-500" style={{ touchAction: 'manipulation' }} />
          </div>

          {showRecurrence && <RecurrenceSection showForm={showForm} handleChange={handleChange} />}

          <div className="grid grid-cols-2 gap-3 relative" style={{ isolation: 'isolate' }}>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Start Time</label>
              <input type="time" value={showForm.startTime || ''} onChange={(e) => handleChange('startTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-orange-500" style={{ touchAction: 'manipulation' }} />
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">End Time</label>
              <input type="time" value={showForm.endTime || ''} onChange={(e) => handleChange('endTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-orange-500" style={{ touchAction: 'manipulation' }} />
            </div>
          </div>

          <div className="relative" style={{ isolation: 'isolate' }}>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Calendar Color</label>
            <div className="flex flex-wrap gap-2">
              {EVENT_COLORS.map(color => (
                <button key={color.value} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleChange('eventColor', color.value); }}
                  className={`w-8 h-8 rounded-full ${color.bg} transition-all ${showForm.eventColor === color.value ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800 scale-110' : 'hover:scale-105'}`}
                  title={color.label} style={{ touchAction: 'manipulation' }} />
              ))}
            </div>
          </div>

          <div className="relative" style={{ isolation: 'isolate' }}>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Notes</label>
            <textarea placeholder="Any notes..." value={showForm.showNotes || ''} onChange={(e) => handleChange('showNotes', e.target.value)} rows={2}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500 resize-none" style={{ touchAction: 'manipulation' }} />
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2 relative" style={{ isolation: 'isolate' }}>
        {editingShow && (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }}
            className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" style={{ touchAction: 'manipulation' }}>Cancel</button>
        )}
        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit(); }} disabled={!showForm.title?.trim()}
          className={`flex-1 py-3 ${config.color} text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2`} style={{ touchAction: 'manipulation' }}>
          {React.createElement(config.icon, { size: 18 })}{editingShow ? 'Save Changes' : `Add ${config.label}`}
        </button>
      </div>

      {editingShow && onDelete && (
        <button type="button" onClick={handleDeleteClick}
          className="w-full py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors flex items-center justify-center gap-2" style={{ touchAction: 'manipulation' }}>
          <Trash2 size={18} />Delete Event
        </button>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">Delete Event?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to delete "{showForm.title || 'this event'}"? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" style={{ touchAction: 'manipulation' }}>Cancel</button>
              <button onClick={handleConfirmDelete} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors" style={{ touchAction: 'manipulation' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowFormComponent;